import React, { useState, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components";
import AppContext from "../../context/context";

// mui components
import IconButton from '@mui/material/IconButton';
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import Switch from "@mui/material/Switch";
import Avatar from "@mui/material/Avatar";
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';

// custom components
import { TableContainerStyled, TableCellStyled, Table, TableBody, TableRow } from "../../components/TableComponents";
import EditChannel from "../../components/Channel/Editchannel";
import Legend from "../../components/Legend";
import WarningModal from "../../components/Warningmodal";
import ChannelLinks from "./ChannelLinks";
import { Text } from "./ChannelDetailDisplayDiv";
import Actions from "../../context/actions";

//services
import { deleteChannel, refreshChannel, uploadLogo, editchannel } from "../../service/channel.service";
import Constants from "../../constants";
import AdminUtil from "../../utils/admin.util";
import { FormatDate } from "../../utils/date.util";

// styled
const HealthDiv = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 4px;
  font-size: 12px;
`;


const ChannelTable = ({ channels, onUpdate }) => {

  // store variable
  const { store, dispatch } = useContext(AppContext);
  const { user } = store;
  const history = useHistory();
  // state vars
  const [_opendelete, setOpendelete] = useState(false);
  const [_openedit, setOpenedit] = useState(false);
  const [_openMore, setOpenMore] = useState(false);
  const [_openLogoPreview, setOpenLogoPreview] = useState(false);
  const [_previewLogo, setPreviewLogo] = useState({ url: "", name: "" });
  const [_channel, setChannel] = useState({});
  const [_opeMenu, setOpenMenu] = React.useState(null);
  const feature_disabled = AdminUtil.isUser(user);
  const logoInputRef = useRef(null);
  const uploadChannelRef = useRef(null);

  const _dispatchRefreshChannel = () => {
    dispatch({
      type: Actions.SET_REFRESH_CHANNEL_FLAG,
    })
  }

  const _setCurrentChannel = async (channelSelected) => {
    return new Promise((resolve) => {
      setChannel(channelSelected);
      setTimeout(() => resolve(true), 100);
    })
  }

  const _openEditChannelModal = async (c) => {
    _closeMoreMenu();
    await _setCurrentChannel(c);
    setOpenedit(true)
  }

  const _openDeleteChannelDialog = async (c) => {
    _closeMoreMenu();
    await _setCurrentChannel(c);
    setOpendelete(true)
  }

  const _openLinkModal = async (c) => {
    await _setCurrentChannel(c)
    setOpenMore(true);
  }


  const _openMoreMenu = async (event, c) => {
    _setCurrentChannel(c)
    setOpenMenu(event.currentTarget);
  };


  const _closeMoreMenu = async () => {
    setOpenMenu(null);
  };

  const _deleteChannel = async () => {
    dispatch({
      type: Actions.SHOW_ALERT,
      payload: {
        type: Constants.alert.SUCCESS,
        message: `Deleting channel ${_channel?.name}`,
      },
    });
    await deleteChannel(_channel["id"]);
    setOpendelete(false);
    _dispatchRefreshChannel()
  };

  const _refreshChannelHealth = async (c) => {
    _closeMoreMenu();
    onUpdate();
    history.replace(`/app/home?domain=${c.domain}&channel=${c.name}`)
    await refreshChannel(c.name);
    dispatch({
      type: Actions.SET_REFRESH_CHANNEL_FLAG,
    })
  }

  const _toggleSwitchStatus = async (c) => {
    onUpdate();
    await editchannel({
      ...c,
      "feature": {
        ...c["feature"],
        "switched_off": !c['feature']['switched_off']
      },
    });
    history.replace(`/app/home?domain=${c.domain}&channel=${c.name}`)
    _dispatchRefreshChannel();
  }


  const _openPreviewWindow = (c) => {
    _closeMoreMenu();
    window.open(c['preview_url'], "blank")
  }

  const _openLogoPreviewModal = (channel) => {
    if (!channel.logo_url) return;
    setPreviewLogo({ url: channel.logo_url, name: channel.name });
    setOpenLogoPreview(true);
  }

  const _triggerLogoUpload = (channel) => {
    _closeMoreMenu();
    uploadChannelRef.current = channel;
    logoInputRef.current?.click();
  }

  const _handleLogoFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !uploadChannelRef.current) return;

    if (file.type !== "image/png") {
      dispatch({
        type: Actions.SHOW_ALERT,
        payload: {
          type: Constants.alert.ERROR,
          message: "Logo must be a PNG image",
        },
      });
      return;
    }

    if (file.size > 1024 * 1024) {
      dispatch({
        type: Actions.SHOW_ALERT,
        payload: {
          type: Constants.alert.ERROR,
          message: "Logo must not exceed 1MB",
        },
      });
      return;
    }

    const result = await uploadLogo(uploadChannelRef.current.name, file);
    if (result === "success") {
      dispatch({
        type: Actions.SHOW_ALERT,
        payload: {
          type: Constants.alert.SUCCESS,
          message: "Logo uploaded successfully",
        },
      });
      _dispatchRefreshChannel();
    } else {
      dispatch({
        type: Actions.SHOW_ALERT,
        payload: {
          type: Constants.alert.ERROR,
          message: "Logo upload failed",
        },
      });
    }
  }

  function formatViews(viewsInput) {
    const views = typeof viewsInput === 'number'
      ? viewsInput
      : parseInt(String(viewsInput).replace(/[^0-9]/g, ''), 10);

    if (!views || views <= 0) return '0';

    if (views >= 1000000) {
      const num = views / 1000000;
      return `${num % 1 === 0 ? num.toFixed(0) : num.toFixed(1)}M`;
    } else if (views >= 1000) {
      const num = views / 1000;
      return `${num % 1 === 0 ? num.toFixed(0) : num.toFixed(1)}K`;
    }

    return `${views}`;
  }


  return (
    <div>
      <TableContainerStyled>
        <Table aria-label="channel-list">
          <TableRow>
            <TableCellStyled align="left">No</TableCellStyled>
            <TableCellStyled align="left">Logo</TableCellStyled>
            <TableCellStyled align="left">Name</TableCellStyled>
            <TableCellStyled align="left">Created Date</TableCellStyled>
            <TableCellStyled align="center">Health</TableCellStyled>
            <TableCellStyled align="center">RTMP Views</TableCellStyled>
            <TableCellStyled align="center">HLS Views</TableCellStyled>
            <TableCellStyled align="center">SRT Views</TableCellStyled>
            <TableCellStyled align="left">Status</TableCellStyled>
            <TableCellStyled align="left">Links</TableCellStyled>
            <TableCellStyled align="left">More</TableCellStyled>
          </TableRow>
          <TableBody>
            {channels.map((channel, index) => {
              const feature = channel.feature;
              return (
                <TableRow key={channel.key + " " + index}>
                  <TableCellStyled align="left">{index + 1}</TableCellStyled>
                  <TableCellStyled align="left">
                    <Avatar
                      alt={channel.name}
                      src={channel.logo_url}
                      variant="square"
                      onClick={() => _openLogoPreviewModal(channel)}
                      sx={{ cursor: channel.logo_url ? "pointer" : "default" }}
                      sizes="small"
                    />
                  </TableCellStyled>
                  <TableCellStyled align="left">{channel.name}</TableCellStyled>
                  <TableCellStyled align="left">{FormatDate(channel['created_at'])}</TableCellStyled>
                  <TableCellStyled align="center">
                    <HealthDiv>
                      {feature.health ? <Legend type="live" /> : <Legend type="idle" />}
                      <p>{feature.health ? "Live" : "Idle"}</p>
                    </HealthDiv>
                  </TableCellStyled>
                  <TableCellStyled align="center">{feature.health ? formatViews(feature['rtmp_views']) : "-"}</TableCellStyled>
                  <TableCellStyled align="center">{formatViews(feature['hls_views'])}</TableCellStyled>
                  <TableCellStyled align="center">{
                    channel["srt_in"]?.length > 0 ? formatViews(feature['srt_views']) : "-"
                  }</TableCellStyled>
                  <TableCellStyled align="center">
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Text>Off</Text>
                      <Switch
                        onChange={() => _toggleSwitchStatus(channel)}
                        size="small"
                        checked={!feature["switched_off"]}
                        disabled={feature_disabled}
                      />
                      <Text>On</Text>
                    </Stack>
                  </TableCellStyled>
                  <TableCellStyled align="left">
                    <Button onClick={() => _openLinkModal(channel)} aria-label="channel-links" sx={{ textDecoration: 'underline', textTransform: 'capitalize' }} size="small" >
                      View Links
                    </Button>
                  </TableCellStyled>
                  <TableCellStyled align="left">
                    <IconButton onClick={(e) => _openMoreMenu(e, channel)} color="primary" aria-label="more-on-channels" component="label">
                      <MoreHorizIcon />
                    </IconButton>
                  </TableCellStyled>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainerStyled>
      <WarningModal
        open={_opendelete}
        onClose={() => setOpendelete(false)}
        message={`Are you sure you want to delete the channel ${_channel?.name} ?`}
        onYes={_deleteChannel}
      />
      <EditChannel
        open={_openedit}
        onClose={() => setOpenedit(false)}
        channel={_channel}
        callback={_dispatchRefreshChannel}
      />
      <ChannelLinks channel={_channel} open={_openMore} onClose={() => setOpenMore(false)} />
      <Dialog
        open={_openLogoPreview}
        onClose={() => setOpenLogoPreview(false)}
        aria-labelledby="logo-preview-title"
      >
        <DialogTitle id="logo-preview-title">{_previewLogo.name}</DialogTitle>
        <DialogContent sx={{ textAlign: "center" }}>
          <img
            src={_previewLogo.url}
            alt={`${_previewLogo.name} logo`}
            style={{ maxWidth: "100%", maxHeight: "400px", objectFit: "contain" }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenLogoPreview(false)} variant="contained" color="primary" disableElevation>
            Close
          </Button>
        </DialogActions>
      </Dialog>
      <input
        ref={logoInputRef}
        type="file"
        accept="image/png,.png"
        style={{ display: "none" }}
        onChange={_handleLogoFileChange}
      />
      <Menu
        id="more-menu"
        anchorEl={_opeMenu}
        open={Boolean(_opeMenu)}
        onClose={_closeMoreMenu}
      >
        <MenuItem disabled={!AdminUtil.isSuperAdmin(user)} onClick={() => _openEditChannelModal(_channel)} >Edit</MenuItem>
        <MenuItem onClick={() => _refreshChannelHealth(_channel)} >Refresh</MenuItem>
        <MenuItem onClick={() => _openPreviewWindow(_channel)} >Preview</MenuItem>
        <MenuItem onClick={() => _triggerLogoUpload(_channel)} >Upload logo</MenuItem>
        <MenuItem disabled={(user['type'] === "USER")} onClick={() => _openDeleteChannelDialog(_channel)} sx={{ color: 'red' }} >Delete</MenuItem>
      </Menu>
    </div>
  );
};

export default ChannelTable;
